import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { AppData, Role, Project, Task, WorkBlock, Capture, ID, DayBlock, DAY_RHYTHM } from "@/lib/types";
import { loadData, saveData, uid } from "@/lib/storage";
import { todayKey, weekKey } from "@/lib/date";

const freshDaily = () => Array.from({ length: 3 }, () => ({ id: uid(), text: "", done: false }));
const buildBlocks = (): DayBlock[] =>
  DAY_RHYTHM.map((b, i) => ({ id: `blk-${i}`, time: b.time, label: b.label, title: "", done: false }));

interface AppContextValue {
  data: AppData;
  // onboarding
  completeOnboarding: (name: string) => void;
  setUserName: (name: string) => void;
  // intention
  setIntention: (text: string) => void;
  // today plan (new)
  ensureToday: () => void;
  setWeeklyOrientations: (texts: string[]) => void;
  setDailyAt: (index: number, text: string) => void;
  toggleDailyAt: (index: number) => void;
  setBlockTitleAt: (index: number, title: string) => void;
  setBlockTimeAt: (index: number, time: string) => void;
  toggleBlockAt: (index: number) => void;
  // roles
  addRole: (name: string, color: string) => Role;
  updateRole: (id: ID, name: string) => void;
  setRoleColor: (id: ID, color: string) => void;
  removeRole: (id: ID) => void;
  moveRole: (id: ID, direction: "up" | "down") => void;
  // projects
  addProject: (name: string, roleId: ID | null) => Project;
  removeProject: (id: ID) => void;
  moveProject: (id: ID, direction: "up" | "down") => void;
  // tasks
  addTask: (text: string, projectId: ID | null) => Task;
  toggleTask: (id: ID) => void;
  moveTask: (id: ID, projectId: ID | null) => void;
  removeTask: (id: ID) => void;
  clearCompletedTasks: () => void;
  // work blocks
  addWorkBlock: (title: string, projectId: ID | null, duration: number) => WorkBlock;
  toggleWorkBlock: (id: ID) => void;
  removeWorkBlock: (id: ID) => void;
  // focus
  setFocusBlock: (id: ID | null) => void;
  // capture
  addCapture: (text: string) => Capture;
  removeCapture: (id: ID) => void;
  captureToTask: (id: ID, projectId: ID | null) => void;
  // reset
  markReset: () => void;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [data, setData] = useState<AppData>(() => loadData());

  useEffect(() => {
    saveData(data);
  }, [data]);

  const value = useMemo<AppContextValue>(() => {
    const patch = (p: Partial<AppData>) => setData((d) => ({ ...d, ...p }));

    return {
      data,
      completeOnboarding: (name) =>
        setData((d) => ({ ...d, onboarded: true, userName: name.trim() })),
      setUserName: (name) => patch({ userName: name.trim() }),
      setIntention: (text) => patch({ intention: { date: todayKey(), text: text.trim() } }),

      ensureToday: () =>
        setData((d) => {
          const tk = todayKey();
          const next = { ...d };
          let changed = false;
          if (d.daily.date !== tk || d.daily.items.length !== 3) {
            next.daily = { date: tk, items: freshDaily() };
            changed = true;
          }
          if (d.flow.date !== tk || d.flow.blocks.length !== 7) {
            next.flow = { date: tk, blocks: buildBlocks() };
            changed = true;
          }
          return changed ? next : d;
        }),

      setWeeklyOrientations: (texts) =>
        setData((d) => ({
          ...d,
          weekly: {
            week: weekKey(),
            items: texts.map((t) => ({ id: uid(), text: t.trim() })),
          },
        })),

      setDailyAt: (index, text) =>
        setData((d) => {
          const items = [...d.daily.items];
          while (items.length < 3) items.push({ id: uid(), text: "", done: false });
          items[index] = { ...items[index], text };
          return { ...d, daily: { date: todayKey(), items } };
        }),

      toggleDailyAt: (index) =>
        setData((d) => {
          const items = [...d.daily.items];
          while (items.length < 3) items.push({ id: uid(), text: "", done: false });
          items[index] = { ...items[index], done: !items[index].done };
          return { ...d, daily: { date: todayKey(), items } };
        }),

      setBlockTitleAt: (index, title) =>
        setData((d) => {
          const blocks = d.flow.blocks.length === 7 ? [...d.flow.blocks] : buildBlocks();
          blocks[index] = { ...blocks[index], title };
          return { ...d, flow: { date: todayKey(), blocks } };
        }),

      setBlockTimeAt: (index, time) =>
        setData((d) => {
          if (!time) return d;
          const blocks = d.flow.blocks.length === 7 ? [...d.flow.blocks] : buildBlocks();
          blocks[index] = { ...blocks[index], time };
          return { ...d, flow: { date: todayKey(), blocks } };
        }),

      toggleBlockAt: (index) =>
        setData((d) => {
          const blocks = d.flow.blocks.length === 7 ? [...d.flow.blocks] : buildBlocks();
          blocks[index] = { ...blocks[index], done: !blocks[index].done };
          return { ...d, flow: { date: todayKey(), blocks } };
        }),

      addRole: (name, color) => {
        const role: Role = { id: uid(), name: name.trim(), color };
        setData((d) => ({ ...d, roles: [...d.roles, role] }));
        return role;
      },
      updateRole: (id, name) =>
        setData((d) => ({
          ...d,
          roles: d.roles.map((r) => (r.id === id ? { ...r, name: name.trim() } : r)),
        })),
      setRoleColor: (id, color) =>
        setData((d) => ({
          ...d,
          roles: d.roles.map((r) => (r.id === id ? { ...r, color } : r)),
        })),
      removeRole: (id) =>
        setData((d) => ({
          ...d,
          roles: d.roles.filter((r) => r.id !== id),
          projects: d.projects.map((p) => (p.roleId === id ? { ...p, roleId: null } : p)),
        })),

      moveRole: (id, direction) =>
        setData((d) => {
          const idx = d.roles.findIndex((r) => r.id === id);
          if (idx < 0) return d;
          const target = direction === "up" ? idx - 1 : idx + 1;
          if (target < 0 || target >= d.roles.length) return d;
          const roles = [...d.roles];
          [roles[idx], roles[target]] = [roles[target], roles[idx]];
          return { ...d, roles };
        }),

      addProject: (name, roleId) => {
        const project: Project = {
          id: uid(),
          name: name.trim(),
          roleId,
          createdAt: new Date().toISOString(),
        };
        setData((d) => ({ ...d, projects: [...d.projects, project] }));
        return project;
      },
      removeProject: (id) =>
        setData((d) => ({
          ...d,
          projects: d.projects.filter((p) => p.id !== id),
          tasks: d.tasks.filter((t) => t.projectId !== id),
          workBlocks: d.workBlocks.filter((w) => w.projectId !== id),
        })),

      moveProject: (id, direction) =>
        setData((d) => {
          const idx = d.projects.findIndex((p) => p.id === id);
          if (idx < 0) return d;
          const target = direction === "up" ? idx - 1 : idx + 1;
          if (target < 0 || target >= d.projects.length) return d;
          const projects = [...d.projects];
          [projects[idx], projects[target]] = [projects[target], projects[idx]];
          return { ...d, projects };
        }),

      addTask: (text, projectId) => {
        const task: Task = {
          id: uid(),
          text: text.trim(),
          projectId,
          done: false,
          createdAt: new Date().toISOString(),
          completedAt: null,
        };
        setData((d) => ({ ...d, tasks: [...d.tasks, task] }));
        return task;
      },
      toggleTask: (id) =>
        setData((d) => ({
          ...d,
          tasks: d.tasks.map((t) =>
            t.id === id
              ? { ...t, done: !t.done, completedAt: !t.done ? new Date().toISOString() : null }
              : t
          ),
        })),
      moveTask: (id, projectId) =>
        setData((d) => ({
          ...d,
          tasks: d.tasks.map((t) => (t.id === id ? { ...t, projectId } : t)),
        })),
      removeTask: (id) =>
        setData((d) => ({ ...d, tasks: d.tasks.filter((t) => t.id !== id) })),
      clearCompletedTasks: () =>
        setData((d) => ({ ...d, tasks: d.tasks.filter((t) => !t.done) })),

      addWorkBlock: (title, projectId, duration) => {
        const block: WorkBlock = {
          id: uid(),
          title: title.trim(),
          projectId,
          duration,
          date: todayKey(),
          done: false,
          createdAt: new Date().toISOString(),
        };
        setData((d) => ({ ...d, workBlocks: [...d.workBlocks, block] }));
        return block;
      },
      toggleWorkBlock: (id) =>
        setData((d) => ({
          ...d,
          workBlocks: d.workBlocks.map((w) => (w.id === id ? { ...w, done: !w.done } : w)),
        })),
      removeWorkBlock: (id) =>
        setData((d) => ({
          ...d,
          workBlocks: d.workBlocks.filter((w) => w.id !== id),
          focusBlockId: d.focusBlockId === id ? null : d.focusBlockId,
        })),

      setFocusBlock: (id) => patch({ focusBlockId: id }),

      addCapture: (text) => {
        const capture: Capture = {
          id: uid(),
          text: text.trim(),
          createdAt: new Date().toISOString(),
        };
        setData((d) => ({ ...d, captures: [capture, ...d.captures] }));
        return capture;
      },
      removeCapture: (id) =>
        setData((d) => ({ ...d, captures: d.captures.filter((c) => c.id !== id) })),
      captureToTask: (id, projectId) =>
        setData((d) => {
          const cap = d.captures.find((c) => c.id === id);
          if (!cap) return d;
          const task: Task = {
            id: uid(),
            text: cap.text,
            projectId,
            done: false,
            createdAt: new Date().toISOString(),
            completedAt: null,
          };
          return {
            ...d,
            tasks: [...d.tasks, task],
            captures: d.captures.filter((c) => c.id !== id),
          };
        }),

      markReset: () => patch({ lastReset: new Date().toISOString() }),
    };
  }, [data]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = (): AppContextValue => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};
